import { pure } from '@core/helpers/pure';
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  forwardRef,
  inject,
  input,
  OnInit,
} from '@angular/core';

import { TuiInputNumber } from '@taiga-ui/kit';
import {
  TuiError,
  TuiCheckbox,
  TuiTextfield,
  TuiNumberFormat,
} from '@taiga-ui/core';
import {
  ControlValueAccessor,
  FormBuilder,
  NG_VALIDATORS,
  NG_VALUE_ACCESSOR,
  ReactiveFormsModule,
  ValidationErrors,
  Validator,
  Validators,
} from '@angular/forms';
import { RequestDeviceProtectiveCaseFormFields } from './types';
import { Nullable } from '@core/helpers/nullable';
import {
  protectiveCaseNumberCrossFieldsValidator,
  protectiveCaseNumberRequiredValidationError,
} from './validators';
import { tap } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { RequestDevice } from '@core/types/RequestDevice';
import { TranslatePipe } from '@ngx-translate/core';

@Component({
  selector: 'x-request-device-protective-case-input',
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => RequestDeviceProtectiveCaseInputComponent),
      multi: true,
    },
    {
      provide: NG_VALIDATORS,
      useExisting: forwardRef(() => RequestDeviceProtectiveCaseInputComponent),
      multi: true,
    },
  ],
  imports: [
    TuiTextfield,
    TuiNumberFormat,
    ReactiveFormsModule,
    TuiError,
    TuiCheckbox,
    TuiInputNumber,
    TranslatePipe,
  ],
  templateUrl: './request-device-protective-case-input.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class RequestDeviceProtectiveCaseInputComponent
  implements ControlValueAccessor, OnInit, Validator, AfterViewInit
{
  private readonly formBuilder = inject(FormBuilder);
  private readonly destroyRef = inject(DestroyRef);
  private onTouched!: (value: any) => void;

  $patchValue = input<false | RequestDevice>(false, { alias: 'patchValue' });

  protectiveCaseNumberRequiredValidationError =
    protectiveCaseNumberRequiredValidationError;
  FormFields = RequestDeviceProtectiveCaseFormFields;
  formGroup = this.formBuilder.group(
    {
      [RequestDeviceProtectiveCaseFormFields.HasProtectiveCase]:
        this.formBuilder.control<boolean>(false, {
          validators: [Validators.required],
          nonNullable: true,
        }),
      [RequestDeviceProtectiveCaseFormFields.ProtectiveCaseNumber]:
        this.formBuilder.control<Nullable<number>>(null),
    },
    { validators: [protectiveCaseNumberCrossFieldsValidator] },
  );

  @pure
  get hasProtectiveCaseField() {
    return this.formGroup.controls[
      RequestDeviceProtectiveCaseFormFields.HasProtectiveCase
    ];
  }

  @pure
  get protectiveCaseNumberField() {
    return this.formGroup.controls[
      RequestDeviceProtectiveCaseFormFields.ProtectiveCaseNumber
    ];
  }

  ngOnInit(): void {
    const patchValue = this.$patchValue();

    if (patchValue) {
      this.formGroup.patchValue({
        ...patchValue,
        hasProtectCase: !!patchValue.hasProtectCase,
      } as Parameters<typeof this.formGroup.patchValue>[0]);
    }
    this.tieProtectiveCaseFieldsValues();
  }

  ngAfterViewInit(): void {
    this.formGroup.updateValueAndValidity();
  }

  private tieProtectiveCaseFieldsValues() {
    this.hasProtectiveCaseField.valueChanges
      .pipe(
        tap(value => {
          if (!value) {
            this.protectiveCaseNumberField.reset();
          }
          this.protectiveCaseNumberField.updateValueAndValidity();
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();
  }

  registerOnChange(fn: any) {
    this.formGroup.valueChanges.subscribe(fn);
  }

  registerOnTouched(fn: any) {
    this.onTouched = fn;
  }

  writeValue(value: any) {
    if (value) {
      this.formGroup.setValue(value, { emitEvent: false });
    }
  }

  setDisabledState(isDisabled: boolean) {
    if (isDisabled) {
      this.formGroup.disable();
    } else {
      this.formGroup.enable();
    }
  }

  validate(): ValidationErrors | null {
    return this.formGroup.valid ? null : { isValid: false };
  }
}
